import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ListItems from './listItems';
import { toogleMenu } from '../../redux/navigationSlice';
import { logoff } from '../../redux/accountSlice';

const drawerWidth = 280;

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: 'rgb(0,98,105)',
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  paddingLeft: 0,
  [theme.breakpoints.down('sm')]: {
    zIndex: theme.zIndex.modal + 1,
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  paddingLeft: '16px !important',
  paddingRight: 24,
  minHeight: '70px !important',
  [theme.breakpoints.down('sm')]: {
    minHeight: '64px !important',
    paddingLeft: '8px !important',
    paddingRight: '8px !important',
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginLeft: 0,
  marginRight: 36,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    transform: 'scale(1.05)',
  },
  transition: 'all 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    marginRight: 16,
  },
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  flexGrow: 1,
  fontWeight: 600,
  letterSpacing: '0.5px',
  fontSize: '1.5rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.25rem',
  },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  width: 40,
  height: 40,
  marginLeft: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    border: '2px solid rgba(255, 255, 255, 0.6)',
  },
}));

const UserNameText = styled(Typography)(({ theme }) => ({
  marginRight: theme.spacing(1),
  fontWeight: 500,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})(({ theme, open, isMobile }) => ({
  '& .MuiDrawer-paper': {
    whiteSpace: 'nowrap',
    width: isMobile ? drawerWidth : (open ? drawerWidth : theme.spacing(7)),
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
    borderRight: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '4px 0 20px rgba(0, 0, 0, 0.05)',
    [theme.breakpoints.up('sm')]: {
      position: 'relative',
      width: open ? drawerWidth : theme.spacing(9),
    },
    [theme.breakpoints.down('sm')]: {
      position: 'fixed',
      width: drawerWidth,
      height: '100%',
      zIndex: theme.zIndex.drawer,
    },
  },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '0 8px',
  ...theme.mixins.toolbar,
  minHeight: '70px !important',
  background: 'rgb(0,98,105)',
  '& .MuiIconButton-root': {
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '64px !important',
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 12,
    marginTop: theme.spacing(1),
    minWidth: 200,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    '& .MuiMenuItem-root': {
      padding: '12px 20px',
      fontSize: '0.95rem',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: 'rgba(0, 98, 105, 0.08)',
        transform: 'translateX(4px)',
      },
      '& .MuiSvgIcon-root': {
        marginRight: theme.spacing(1.5),
        fontSize: '1.2rem',
      },
    },
  },
}));

const handleAccountOptionsOpen = (event, setAnchorEl) => {
  setAnchorEl(event.currentTarget);
};

const handleAccountOpionsClose = (setAnchorEl) => {
  setAnchorEl(null);
};

const logoffUser = (setAnchorEl, logoffFunction) => {
  logoffFunction();
  setAnchorEl(null);
};

const handleNavigationMenuOpen = (toogleNavigation) => {
  toogleNavigation(true);
};

const handleNavigationMenuClose = (toogleNavigation) => {
  toogleNavigation(false);
};

const Navigation = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Redux state selectors
  const { isMenuExpanded: reduxIsMenuExpanded } = useSelector(state => state.navigation);
  const user = useSelector(state => state.account.user);
  
  // Redux actions
  const toogleNavigation = (isMenuExpanded) => dispatch(toogleMenu(isMenuExpanded));
  const logoffAction = () => dispatch(logoff());
  
  // Local state for menu anchor (UI state only)
  const [anchorEl, setAnchorEl] = useState(null);
  // Use Redux state directly for menu expansion
  const isMenuExpanded = reduxIsMenuExpanded;

  
  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const renderMenu = (
    <StyledMenu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={Boolean(anchorEl)}
      onClose={() => handleAccountOpionsClose(setAnchorEl)}
    >
      <MenuItem onClick={() => handleAccountOpionsClose(setAnchorEl)}>
        <PersonIcon />
        Minha conta
      </MenuItem>
      <MenuItem onClick={() => logoffUser(setAnchorEl, logoffAction)}>
        <LogoutIcon />
        Sair
      </MenuItem>
    </StyledMenu>
  );

  let loggedScreen;

  if (!!user)
    loggedScreen =
      <>
        <StyledAppBar position="absolute">
          <StyledToolbar>
            <StyledIconButton
              color="inherit"
              aria-label="Open drawer"
              onClick={() => {
                if (isMobile && isMenuExpanded) {
                  handleNavigationMenuClose(toogleNavigation);
                } else {
                  handleNavigationMenuOpen(toogleNavigation);
                }
              }}
              sx={{ display: isMenuExpanded && !isMobile ? 'none' : 'flex' }}
            >
              <MenuIcon />
            </StyledIconButton>
            {!isMobile && (
              <StyledIconButton
                color="inherit"
                aria-label="Close drawer"
                onClick={() => handleNavigationMenuClose(toogleNavigation)}
                sx={{ display: !isMenuExpanded ? 'none' : 'flex' }}
              >
                <ChevronLeftIcon />
              </StyledIconButton>
            )}
            <StyledTitle variant="h6" color="inherit" noWrap>
              Financeiro
            </StyledTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <UserNameText align="right">
                {user.name}
              </UserNameText>
              <UserAvatar
                onClick={(event) => handleAccountOptionsOpen(event, setAnchorEl)}
                aria-owns={isMenuExpanded ? 'material-appbar' : undefined}
                aria-haspopup="true"
              >
                {getUserInitials(user.name)}
              </UserAvatar>
            </Box>
          </StyledToolbar>
        </StyledAppBar>
        {renderMenu}
        {isMobile ? (
          // Mobile: Temporary drawer
          <Drawer
            variant="temporary"
            open={isMenuExpanded}
            onClose={() => handleNavigationMenuClose(toogleNavigation)}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
                borderRight: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '4px 0 20px rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <DrawerHeader>
              <IconButton onClick={() => handleNavigationMenuClose(toogleNavigation)}>
                <ChevronLeftIcon />
              </IconButton>
            </DrawerHeader>
            <Divider sx={{ opacity: 0.6 }} />
            <ListItems isMenuExpanded={true} />
          </Drawer>
        ) : (
          // Desktop: Permanent drawer
          <StyledDrawer
            variant="permanent"
            open={isMenuExpanded}
            isMobile={false}
          >
            <DrawerHeader>
              <IconButton onClick={() => handleNavigationMenuClose(toogleNavigation)}>
                <ChevronLeftIcon />
              </IconButton>
            </DrawerHeader>
            <Divider sx={{ opacity: 0.6 }} />
            <ListItems isMenuExpanded={isMenuExpanded} />
          </StyledDrawer>
        )}
      </>
  else
    loggedScreen = <>
      <StyledAppBar position="absolute">
        <StyledToolbar>
          <StyledTitle variant="h6" color="inherit" noWrap>
            Financeiro
          </StyledTitle>
        </StyledToolbar>
      </StyledAppBar>
    </>

  return (
    <>
      {loggedScreen}
    </>
  );
}

export default Navigation;